import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import CanchaForm from "./components/CanchaForm";
import CanchasManager from "./components/CanchasManager";
import BloquesManager from "./components/BloquesManager";
import ComprobanteCierre from "./components/ComprobanteCierre";
import FeedbackMessage from "./components/FeedbackMessage";
import MovimientoForm from "./components/MovimientoForm";
import ReportesPanel from "./components/ReportesPanel";
import ReservaForm from "./components/ReservaForm";
import ReservasList from "./components/ReservasList";
import UsersManager from "./components/UsersManager";
import { buildReporteCsvContent } from "./utils/reportCsv";
import {
  cancelReserva,
  closeCierreDiario,
  createCancha,
  createBloque,
  createMovimiento,
  createReserva,
  createUsuario,
  deleteBloque,
  deleteCancha,
  deleteMovimientoManual,
  deleteReserva,
  fetchCierreDiario,
  fetchCierreDetalle,
  fetchDashboardData,
  fetchBloques,
  fetchMe,
  fetchUsuarios,
  fetchReportes,
  login,
  logout,
  markReservaPagada,
  updateBloqueCancha,
  updateUsuarioEstado,
  updateUsuarioPassword,
  updateUsuarioRol,
  deleteUsuario,
  updateReserva,
  updateCancha,
} from "./services/api";

const AUTH_TOKEN_KEY = "ctrlrentacancha_token";

const _d = new Date();
const hoy = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, "0")}-${String(_d.getDate()).padStart(2, "0")}`;

const reservaInicial = {
  cliente: "",
  rucCedula: "",
  direccion: "",
  celular: "",
  mail: "",
  cancha: "",
  fecha: hoy,
  hora: "",
  duracionMin: 60,
  monto: "",
  pagado: true,
};

const canchaInicial = {
  nombre: "",
  valorHora: "",
  esSubcancha: false,
  parentCanchaId: "",
};

const movimientoInicial = {
  tipo: "egreso",
  concepto: "",
  monto: "",
  fecha: hoy,
};

const reporteInicial = {
  total_reservas: 0,
  reservas_pagadas: 0,
  reservas_pendientes: 0,
  reservas_canceladas: 0,
  ingresos: 0,
  egresos: 0,
  saldo: 0,
  desglose_por_cancha: [],
  cierres_diarios: [],
  cierre_diario: null,
};

function App() {
  const reservaFormRef = useRef(null);
  const clienteInputRef = useRef(null);
  const [logoSrc, setLogoSrc] = useState("/escudo.jpeg");
  const [reservas, setReservas] = useState([]);
  const [fechaReservas, setFechaReservas] = useState(hoy);
  const [movimientos, setMovimientos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [canchas, setCanchas] = useState([]);
  const [canchaItems, setCanchaItems] = useState([]);
  const [bloques, setBloques] = useState([]);
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  const [mostrarPanelCanchas, setMostrarPanelCanchas] = useState(false);
  const [mostrarPanelUsuarios, setMostrarPanelUsuarios] = useState(false);
  const [cierreDetalle, setCierreDetalle] = useState(null);
  const [rangoReporte, setRangoReporte] = useState({ fechaInicio: hoy, fechaFin: hoy });
  const [reporteGlobal, setReporteGlobal] = useState(reporteInicial);
  const [cierreHoy, setCierreHoy] = useState(null);
  const [reservaForm, setReservaForm] = useState(reservaInicial);
  const [canchaForm, setCanchaForm] = useState(canchaInicial);
  const [editandoCanchaId, setEditandoCanchaId] = useState(null);
  const [nombreCanchaEditando, setNombreCanchaEditando] = useState("");
  const [valorHoraCanchaEditando, setValorHoraCanchaEditando] = useState("");
  const [movimientoForm, setMovimientoForm] = useState(movimientoInicial);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY) || "");
  const [authUser, setAuthUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });

  const cargarDatos = useCallback(async () => {
    const data = await fetchDashboardData(fechaReservas);
    setReservas(data.reservas);
    setMovimientos(data.movimientos);
    setCanchas(data.canchas || []);
    setCanchaItems(data.canchaItems || []);
    setReservaForm((prev) => {
      const canchaSeleccionada = (prev.cancha && (data.canchas || []).includes(prev.cancha))
        ? prev.cancha
        : (data.canchas || [])[0] || "";

      const canchaItem = (data.canchaItems || []).find((item) => item.nombre === canchaSeleccionada);
      const valorHora = Number(canchaItem?.valor_hora || 0);
      const montoCalculado = valorHora > 0
        ? ((valorHora * Number(prev.duracionMin || 60)) / 60).toFixed(2)
        : prev.monto;

      return {
        ...prev,
        cancha: canchaSeleccionada,
        monto: montoCalculado,
      };
    });
  }, [fechaReservas]);

  const cargarUsuarios = useCallback(async () => {
    const data = await fetchUsuarios();
    setUsuarios(data.users || []);
  }, []);

  const cargarCierreHoy = useCallback(async () => {
    const data = await fetchCierreDiario(hoy);
    setCierreHoy(data.cierre || null);
  }, []);

  const cargarBloques = useCallback(async () => {
    const data = await fetchBloques();
    setBloques(data.bloques || []);
  }, []);

  const cargarReporte = useCallback(async (fechaInicio, fechaFin) => {
    const data = await fetchReportes(fechaInicio, fechaFin);
    setReporteGlobal({
      ...reporteInicial,
      ...data,
      desglose_por_cancha: data.desglose_por_cancha || [],
    });
  }, []);

  const recargarVista = useCallback(async () => {
    await Promise.all([
      cargarDatos(),
      cargarBloques(),
      cargarReporte(rangoReporte.fechaInicio, rangoReporte.fechaFin),
      cargarCierreHoy(),
    ]);
    setAvailabilityRefreshKey((k) => k + 1);
  }, [cargarBloques, cargarCierreHoy, cargarDatos, cargarReporte, rangoReporte.fechaInicio, rangoReporte.fechaFin]);

  useEffect(() => {
    const bootstrapAuth = async () => {
      if (!authToken) {
        setAuthLoading(false);
        return;
      }

      try {
        const data = await fetchMe(authToken);
        setAuthUser(data.user);
      } catch (_error) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        setAuthToken("");
        setAuthUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    bootstrapAuth();
  }, [authToken]);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    cargarDatos();
    cargarBloques();
    cargarReporte(hoy, hoy);
    cargarCierreHoy();
    if (authUser.rol === "admin") {
      cargarUsuarios();
    }
  }, [authUser, cargarBloques, cargarCierreHoy, cargarDatos, cargarReporte, cargarUsuarios]);

  useEffect(() => {
    if (!authUser) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (document.hidden) {
        return;
      }

      recargarVista().catch(() => {
      });
    }, 15000);

    return () => clearInterval(intervalId);
  }, [authUser, recargarVista]);

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setMensaje("");
    setError("");

    try {
      const data = await login(loginForm);
      localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
      setAuthToken(data.access_token);
      setAuthUser(data.user);
      setLoginForm({ username: "", password: "" });
        setMensaje(`Bienvenido, ${data.user.username}`);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleLogout = async () => {
    setMensaje("");
    setError("");
    try {
      if (authToken) {
        await logout(authToken);
      }
    } catch (_error) {
    } finally {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setAuthToken("");
      setAuthUser(null);
      setReservas([]);
      setMovimientos([]);
      setCanchaItems([]);
      setCanchas([]);
      setBloques([]);
      setReporteGlobal(reporteInicial);
      setMensaje("Sesión cerrada");
    }
  };

  const handleRangoReporteChange = (event) => {
    const { name, value } = event.target;
    setRangoReporte((prev) => ({ ...prev, [name]: value }));
  };

  const handleFechaReservasChange = (event) => {
    setFechaReservas(event.target.value);
  };

  const handleCreateUsuario = async (payload) => {
    setMensaje("");
    setError("");
    try {
      await createUsuario(payload);
      await cargarUsuarios();
      setMensaje("Usuario creado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleToggleUsuarioStatus = async (userId, activo) => {
    setMensaje("");
    setError("");
    try {
      await updateUsuarioEstado(userId, activo);
      await cargarUsuarios();
      setMensaje("Estado de usuario actualizado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleChangeUsuarioPassword = async (userId, password) => {
    setMensaje("");
    setError("");
    try {
      await updateUsuarioPassword(userId, password);
      setMensaje("Contraseña actualizada");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleChangeUsuarioRol = async (userId, rol) => {
    setMensaje("");
    setError("");
    try {
      await updateUsuarioRol(userId, rol);
      await cargarUsuarios();
      setMensaje("Rol actualizado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleDeleteUsuario = async (userId) => {
    setMensaje("");
    setError("");
    try {
      await deleteUsuario(userId);
      await cargarUsuarios();
      setMensaje("Usuario eliminado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleConsultarReporte = async () => {
    setMensaje("");
    setError("");
    try {
      await cargarReporte(rangoReporte.fechaInicio, rangoReporte.fechaFin);
      setMensaje("Reporte actualizado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleCloseCierreDiario = async () => {
    if (rangoReporte.fechaInicio !== rangoReporte.fechaFin) {
      setError("Para cerrar un día, consulta primero un reporte de una sola fecha");
      return;
    }

    const fechaCierre = rangoReporte.fechaInicio;
    const primerConfirm = window.confirm(`¿Cerrar formalmente la caja del día ${fechaCierre}?`);
    if (!primerConfirm) {
      return;
    }

    const segundoConfirm = window.confirm(
      "Segundo aviso: el cierre diario bloquea nuevas modificaciones para esa fecha y guardará saldo de apertura/cierre. ¿Confirmas?"
    );
    if (!segundoConfirm) {
      return;
    }

    setMensaje("");
    setError("");
    try {
      await closeCierreDiario(fechaCierre);
      await Promise.all([
        cargarReporte(rangoReporte.fechaInicio, rangoReporte.fechaFin),
        cargarCierreHoy(),
        cargarDatos(),
      ]);
      setMensaje("Cierre diario registrado correctamente");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleVerComprobante = async (fecha) => {
    setMensaje("");
    setError("");
    try {
      const data = await fetchCierreDetalle(fecha);
      setCierreDetalle(data);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleExportReporteCsv = () => {
    const csvContent = buildReporteCsvContent({
      rango: rangoReporte,
      reporte: reporteGlobal,
      movimientos,
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `movimientos_caja_${rangoReporte.fechaInicio}_${rangoReporte.fechaFin}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const goToNuevaReserva = () => {
    reservaFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      clienteInputRef.current?.focus();
    }, 350);
  };

  const handleReservaChange = (event) => {
    const { name, value, type, checked } = event.target;
    setReservaForm((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      const nextForm = {
        ...prev,
        [name]: nextValue,
      };

      if (name === "cancha" || name === "duracionMin") {
        const canchaSeleccionada = canchaItems.find((item) => item.nombre === nextForm.cancha);
        const valorHora = Number(canchaSeleccionada?.valor_hora || 0);
        if (valorHora > 0) {
          const montoCalculado = (valorHora * Number(nextForm.duracionMin || 60)) / 60;
          nextForm.monto = montoCalculado.toFixed(2);
        }
      }

      return nextForm;
    });
  };

  const handleMovimientoChange = (event) => {
    const { name, value } = event.target;
    setMovimientoForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCanchaChange = (event) => {
    const { name, value } = event.target;
    setCanchaForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReservaSubmit = async (event) => {
    event.preventDefault();
    setMensaje("");
    setError("");

    try {
      await createReserva(reservaForm);
      setReservaForm(reservaInicial);
      setMensaje("Reserva guardada correctamente");
      await recargarVista();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleMovimientoSubmit = async (event) => {
    event.preventDefault();
    setMensaje("");
    setError("");

    try {
      await createMovimiento(movimientoForm);
      setMovimientoForm(movimientoInicial);
      setMensaje("Movimiento guardado correctamente");
      await cargarDatos();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleCanchaSubmit = async (event) => {
    event.preventDefault();
    setMensaje("");
    setError("");

    try {
      await createCancha({ nombre: canchaForm.nombre, valor_hora: canchaForm.valorHora, parent_cancha_id: canchaForm.esSubcancha ? Number(canchaForm.parentCanchaId) : null });
      setCanchaForm(canchaInicial);
      setMensaje("Cancha guardada correctamente");
      await cargarDatos();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleStartEditCancha = (cancha) => {
    setEditandoCanchaId(cancha.id);
    setNombreCanchaEditando(cancha.nombre);
    setValorHoraCanchaEditando(String(cancha.valor_hora ?? ""));
  };

  const handleCancelEditCancha = () => {
    setEditandoCanchaId(null);
    setNombreCanchaEditando("");
    setValorHoraCanchaEditando("");
  };

  const handleSaveEditCancha = async (canchaId) => {
    setMensaje("");
    setError("");

    const nombre = nombreCanchaEditando.trim();
    if (!nombre) {
      setError("El nombre de la cancha es obligatorio");
      return;
    }

    const valorHora = Number(valorHoraCanchaEditando);
    if (!Number.isFinite(valorHora) || valorHora <= 0) {
      setError("El valor por hora debe ser mayor a 0");
      return;
    }

    try {
      await updateCancha(canchaId, { nombre, valor_hora: valorHora });
      setMensaje("Cancha actualizada correctamente");
      handleCancelEditCancha();
      await cargarDatos();
    } catch (requestError) {
      if (requestError.status === 409) {
        const confirmar = window.confirm(`${requestError.message}\n\n¿Deseas confirmar la edición?`);
        if (confirmar) {
          try {
            await updateCancha(canchaId, { nombre, valor_hora: valorHora }, true);
            setMensaje("Cancha actualizada correctamente");
            handleCancelEditCancha();
            await cargarDatos();
            return;
          } catch (forceError) {
            setError(forceError.message);
            return;
          }
        }
      }
      setError(requestError.message);
    }
  };

  const handleDeleteCancha = async (canchaId) => {
    setMensaje("");
    setError("");

    const confirmarPrimeraVez = window.confirm("¿Seguro que deseas eliminar esta cancha?");
    if (!confirmarPrimeraVez) {
      return;
    }

    try {
      await deleteCancha(canchaId);
      setMensaje("Cancha eliminada correctamente");
      await cargarDatos();
    } catch (requestError) {
      if (requestError.status === 409) {
        const confirmarSegundaVez = window.confirm(`${requestError.message}\n\n¿Confirmas la eliminación definitiva?`);
        if (confirmarSegundaVez) {
          try {
            await deleteCancha(canchaId, true);
            setMensaje("Cancha eliminada correctamente");
            await cargarDatos();
            return;
          } catch (forceError) {
            setError(forceError.message);
            return;
          }
        }
      }
      setError(requestError.message);
    }
  };

  const handleCreateBloque = async (payload) => {
    setMensaje("");
    setError("");
    try {
      await createBloque(payload);
      await cargarBloques();
      setAvailabilityRefreshKey((k) => k + 1);
      setMensaje("Bloque creado correctamente");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleDeleteBloque = async (bloqueId, descripcion) => {
    const confirmar = window.confirm(`¿Eliminar bloque interno "${descripcion}"?`);
    if (!confirmar) {
      return;
    }
    setMensaje("");
    setError("");
    try {
      await deleteBloque(bloqueId);
      await cargarBloques();
      setAvailabilityRefreshKey((k) => k + 1);
      setMensaje("Bloque eliminado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleCancelReserva = async (reservaId) => {
    setMensaje("");
    setError("");

    const primerConfirm = window.confirm("¿Seguro que deseas cancelar esta reserva?");
    if (!primerConfirm) {
      return;
    }

    const segundoConfirm = window.confirm(
      "Segundo aviso: al cancelar, la reserva dejará de estar activa y este cambio afectará el reporte. ¿Confirmas la cancelación?"
    );
    if (!segundoConfirm) {
      return;
    }

    try {
      await cancelReserva(reservaId);
      setMensaje("Reserva cancelada correctamente");
      await recargarVista();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleDeleteReserva = async (reserva) => {
    const primerConfirm = window.confirm(
      `¿Eliminar definitivamente la reserva de ${reserva.cliente}?\nEsta acción borra el registro de la base de datos y no se puede deshacer.`
    );
    if (!primerConfirm) return;

    const segundoConfirm = window.confirm(
      `Segundo aviso: ¿Confirmas la eliminación total del registro de ${reserva.cliente} (${reserva.fecha} · ${reserva.cancha})?`
    );
    if (!segundoConfirm) return;

    setMensaje("");
    setError("");
    try {
      await deleteReserva(reserva.id);
      setMensaje("Reserva eliminada definitivamente");
      await recargarVista();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleDeleteMovimientoManual = async (movimiento) => {
    const primerConfirm = window.confirm(
      `¿Eliminar el movimiento manual "${movimiento.concepto}"?\nEsta acción no se puede deshacer.`
    );
    if (!primerConfirm) return;

    const segundoConfirm = window.confirm(
      `Segundo aviso: ¿Confirmas eliminar este ${movimiento.tipo} manual por $${Number(movimiento.monto).toFixed(2)} del ${movimiento.fecha}?`
    );
    if (!segundoConfirm) return;

    setMensaje("");
    setError("");
    try {
      await deleteMovimientoManual(movimiento.id);
      await Promise.all([
        cargarDatos(),
        cargarReporte(rangoReporte.fechaInicio, rangoReporte.fechaFin),
      ]);
      setMensaje("Movimiento manual eliminado");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleMarkReservaPagada = async (reservaId) => {
    setMensaje("");
    setError("");

    try {
      await markReservaPagada(reservaId);
      setMensaje("Reserva marcada como pagada");
      await recargarVista();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleUpdateReserva = async (reservaId, payload) => {
    setMensaje("");
    setError("");

    try {
      await updateReserva(reservaId, payload);
      setMensaje("Reserva actualizada correctamente");
      await recargarVista();
    } catch (requestError) {
      setError(requestError.message);
      throw requestError;
    }
  };

  if (authLoading) {
    return (
      <main className="app-shell">
        <header className="hero">
          <div className="hero-content">
            <div>
              <p className="eyebrow">Control operativo</p>
              <h1>Rentas de Canchas</h1>
              <p className="hero-copy">Cargando sesión...</p>
            </div>
          </div>
        </header>
      </main>
    );
  }

  if (!authUser) {
    return (
      <main className="app-shell">
        <header className="hero">
          <div className="hero-content">
            <div>
              <p className="eyebrow">Control operativo</p>
              <h1>Rentas de Canchas</h1>
              <p className="hero-copy">Inicia sesión para continuar.</p>
            </div>
          </div>
        </header>

        <FeedbackMessage message={mensaje} type="success" />
        <FeedbackMessage message={error} type="error" />

        <section className="panel-grid single-column">
          <article className="panel">
            <h2>Iniciar sesión</h2>
            <form className="form-grid" onSubmit={handleLoginSubmit}>
              <label>
                <span className="visually-hidden">Usuario</span>
                <input
                  name="username"
                  value={loginForm.username}
                  onChange={handleLoginChange}
                  placeholder="Usuario"
                  aria-label="Usuario"
                  required
                />
              </label>
              <label>
                <span className="visually-hidden">Contraseña</span>
                <input
                  type="password"
                  name="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  placeholder="Contraseña"
                  aria-label="Contraseña"
                  required
                />
              </label>
              <button type="submit">Ingresar</button>
            </form>
          </article>
        </section>
      </main>
    );
  }

  const isAdmin = authUser.rol === "admin";
  const reservasSoloLectura = fechaReservas !== hoy;
  const diaOperativoCerrado = Boolean(cierreHoy?.cerrado);
  const bloqueoReservaActual = diaOperativoCerrado && reservaForm.fecha === hoy;
  const bloqueoMovimientoActual = diaOperativoCerrado && movimientoForm.fecha === hoy;
  const reservasBloqueadas = reservasSoloLectura || (fechaReservas === hoy && diaOperativoCerrado);

  return (
    <main className="app-shell">
      <header className="hero">
        <div className="hero-content">
          <div>
          <p className="eyebrow">Control operativo</p>
          <h1>Rentas de Canchas</h1>
          <p className="hero-copy">
            Administra reservas, canchas, entradas y salidas de efectivo.
          </p>
          <p className="hero-copy">Usuario: {authUser.username} · Rol: {authUser.rol}</p>
          <button type="button" className="secondary-button" onClick={handleLogout}>
            Cerrar sesión
          </button>
          </div>
          <img
            src={logoSrc}
            alt="Escudo"
            className="hero-logo"
            onError={() => {
              if (logoSrc === "/escudo.jpeg") {
                setLogoSrc("/escudo2.jpeg");
                return;
              }
              if (logoSrc === "/escudo2.jpeg") {
                setLogoSrc("/escudo1.png");
                return;
              }
              setLogoSrc("/logo192.png");
            }}
          />
        </div>
      </header>

      {(mensaje || error) ? (
        <div className="feedback-stack">
          <FeedbackMessage message={mensaje} type="success" onClose={() => setMensaje("")} />
          <FeedbackMessage message={error} type="error" onClose={() => setError("")} />
        </div>
      ) : null}

      <section ref={reservaFormRef} className="panel-grid single-column">
        <ReservaForm
          form={reservaForm}
          canchas={canchas}
          reservas={reservas}
          bloques={bloques}
          disabled={bloqueoReservaActual}
          onChange={handleReservaChange}
          onSubmit={handleReservaSubmit}
          clienteInputRef={clienteInputRef}
        />
      </section>

      {isAdmin ? (
        <section className="panel-grid two-columns">
          <div className="stack-panel">
            <button
              type="button"
              className="toggle-client-fields"
              onClick={() => setMostrarPanelCanchas((prev) => !prev)}
            >
              {mostrarPanelCanchas ? "Ocultar sección canchas" : "Mostrar sección canchas"}
              <span className="toggle-arrow">{mostrarPanelCanchas ? "⌃" : "⌄"}</span>
            </button>

            {mostrarPanelCanchas ? (
              <>
                <CanchaForm form={canchaForm} onChange={handleCanchaChange} onSubmit={handleCanchaSubmit} canchas={canchaItems} />
                <CanchasManager
                  canchas={canchaItems}
                  editandoId={editandoCanchaId}
                  nombreEditando={nombreCanchaEditando}
                  valorHoraEditando={valorHoraCanchaEditando}
                  onStartEdit={handleStartEditCancha}
                  onChangeEdit={setNombreCanchaEditando}
                  onChangeValorHoraEdit={setValorHoraCanchaEditando}
                  onSaveEdit={handleSaveEditCancha}
                  onCancelEdit={handleCancelEditCancha}
                  onDelete={handleDeleteCancha}
                />
              </>
            ) : null}
          </div>
          <MovimientoForm
            form={movimientoForm}
            disabled={bloqueoMovimientoActual}
            onChange={handleMovimientoChange}
            onSubmit={handleMovimientoSubmit}
          />
        </section>
      ) : (
        <section className="panel-grid single-column">
          <MovimientoForm
            form={movimientoForm}
            disabled={bloqueoMovimientoActual}
            onChange={handleMovimientoChange}
            onSubmit={handleMovimientoSubmit}
          />
        </section>
      )}

      {isAdmin ? (
        <section className="panel-grid single-column">
          <BloquesManager
            canchas={canchas}
            bloques={bloques}
            onCreateBloque={handleCreateBloque}
            onDeleteBloque={handleDeleteBloque}
          />
        </section>
      ) : null}

      <section className="panel-grid single-column">
        <ReservasList
          reservas={reservas}
          canchas={canchas}
          canchaItems={canchaItems}
          fechaFiltro={fechaReservas}
          fechaHoy={hoy}
          soloLectura={reservasBloqueadas}
          onFechaFiltroChange={handleFechaReservasChange}
          onCancel={handleCancelReserva}
          onDelete={handleDeleteReserva}
          onMarkPagada={handleMarkReservaPagada}
          onUpdate={handleUpdateReserva}
          canDeleteDefinitivo={isAdmin}
        />
      </section>

      <ReportesPanel
        rango={rangoReporte}
        reporte={reporteGlobal}
        cierreDiario={reporteGlobal.cierre_diario}
        cierresDiarios={reporteGlobal.cierres_diarios}
        movimientos={movimientos}
        canDeleteManual={isAdmin}
        canManageClose={isAdmin}
        onDeleteMovimientoManual={handleDeleteMovimientoManual}
        onChangeRango={handleRangoReporteChange}
        onConsultar={handleConsultarReporte}
        onCloseDiario={handleCloseCierreDiario}
        onExportCsv={handleExportReporteCsv}
        onVerComprobante={isAdmin ? handleVerComprobante : null}
      />

      {isAdmin ? (
        <section className="panel-grid single-column">
          <button
            type="button"
            className="toggle-client-fields"
            onClick={() => setMostrarPanelUsuarios((prev) => !prev)}
          >
            {mostrarPanelUsuarios ? "Ocultar gestión de usuarios" : "Mostrar gestión de usuarios"}
            <span className="toggle-arrow">{mostrarPanelUsuarios ? "⌃" : "⌄"}</span>
          </button>

          {mostrarPanelUsuarios ? (
            <UsersManager
              users={usuarios}
              onCreateUser={handleCreateUsuario}
              onToggleUserStatus={handleToggleUsuarioStatus}
              onChangeUserPassword={handleChangeUsuarioPassword}
              onChangeUserRol={handleChangeUsuarioRol}
              onDeleteUser={handleDeleteUsuario}
            />
          ) : null}
        </section>
      ) : null}

      <button type="button" className="floating-reserva-button" onClick={goToNuevaReserva}>
        + Nueva reserva
      </button>

      {cierreDetalle ? (
        <ComprobanteCierre
          detalle={cierreDetalle}
          onCerrar={() => setCierreDetalle(null)}
        />
      ) : null}
    </main>
  );
}

export default App;